<template>
  <div
    v-if="modelValue && canManage"
    class="modal-overlay"
    @click.self="closeModal"
  >
    <section class="modal-content" role="dialog" aria-modal="true">
      <header class="modal-header">
        <h2>
          <IconSvg name="link-solid-full" size="md" />
          しおりを共有
        </h2>
        <button class="close-btn" type="button" @click="closeModal">×</button>
      </header>

      <div class="modal-body">
        <p class="description">
          共有リンクを送った相手は、ログインなしで予定や持ち物、割り勘を編集できます。
        </p>

        <div v-if="errorMessage" class="alert alert-error">
          {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="alert alert-success">
          {{ successMessage }}
        </div>

        <div v-if="loadingLinks" class="loading-state">
          共有リンクを確認しています...
        </div>

        <template v-else>
          <button
            v-if="!createdLink && !activeLink"
            class="btn btn-primary create-button"
            type="button"
            :disabled="loading"
            @click="canUseNativeShare ? openNativeShare() : ensureShareLink()"
          >
            {{
              loading
                ? "準備中..."
                : canUseNativeShare
                  ? "共有する"
                  : "共有リンクを作成"
            }}
          </button>

          <div v-else-if="createdLink" class="share-panel">
            <p class="one-time-notice">
              このリンクは今だけ表示されています。共有またはコピーしてから閉じてください。
            </p>
            <p class="permission-text">権限: 編集可能</p>
            <label class="link-label">共有リンク</label>
            <div class="link-row">
              <input
                class="link-input"
                type="text"
                :value="createdLink"
                readonly
                @click="$event.target.select()"
              />
              <button class="copy-button" type="button" @click="copyLink">
                <IconSvg name="copy-solid-full" size="sm" />
                コピー
              </button>
            </div>

            <button
              v-if="canUseNativeShare"
              class="native-share-button"
              type="button"
              @click="openNativeShare"
            >
              共有する
            </button>
          </div>

          <div v-else class="share-panel metadata-panel">
            <p class="issued-status">共有リンクは発行済みです。</p>
            <p class="permission-text">
              権限:
              {{
                activeLink.permissionType === "view"
                  ? "閲覧専用"
                  : "編集可能"
              }}
            </p>
            <p class="description">
              安全のため既存リンクは再表示できません。必要な場合は再発行してください。
            </p>
            <p v-if="activeLink.expiresAt" class="expiry-text">
              有効期限: {{ formatExpiry(activeLink.expiresAt) }}
            </p>
            <div class="metadata-actions">
              <button
                class="btn btn-secondary"
                type="button"
                :disabled="loading"
                @click="rotateLink"
              >
                新しいリンクを発行
              </button>
              <button
                class="btn revoke-button"
                type="button"
                :disabled="loading"
                @click="revokeLink"
              >
                共有リンクを無効化
              </button>
            </div>
          </div>
        </template>
      </div>

      <footer class="modal-footer">
        <button class="btn btn-secondary" type="button" @click="closeModal">
          閉じる
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import { useShioriStore } from "@/store/shiori-store";

export default {
  name: "ShareShioriModal",
  components: { IconSvg },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    shioriId: {
      type: String,
      default: null,
    },
    canManage: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const shioriStore = useShioriStore();
    const loading = ref(false);
    const loadingLinks = ref(false);
    const createdLink = ref("");
    const activeLink = ref(null);
    const successMessage = ref("");
    const errorMessage = ref("");

    const shareText = "Rabiitripの旅行しおりを共有します";
    const canUseNativeShare = computed(
      () => typeof navigator !== "undefined" && Boolean(navigator.share),
    );
    const getErrorMessage = (error, fallback) =>
      typeof error.response?.data?.detail === "string"
        ? error.response.data.detail
        : fallback;
    const normalizePermissionType = (value) => {
      const permission = String(value || "edit").trim().toLowerCase();
      return permission === "view" || permission === "read" ? "view" : "edit";
    };
    const normalizeActiveLink = (metadata) => {
      if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
        return null;
      }
      const tokenId = metadata.TOKEN_ID || metadata.token_id;
      if (typeof tokenId !== "string" || !tokenId.trim()) return null;
      return {
        tokenId: tokenId.trim(),
        expiresAt: metadata.EXPIRES_AT || metadata.expires_at || null,
        permissionType: normalizePermissionType(
          metadata.PERMISSION_TYPE || metadata.permission_type,
        ),
      };
    };

    const clearMessagesLater = () => {
      window.setTimeout(() => {
        successMessage.value = "";
      }, 3000);
    };

    const fetchExistingLink = async () => {
      if (!props.canManage || !props.shioriId) return;
      loadingLinks.value = true;
      errorMessage.value = "";
      createdLink.value = "";
      try {
        const links = await shioriStore.getShareLinks(props.shioriId);
        const active = (Array.isArray(links) ? links : []).find((link) => {
          const purpose = link.PURPOSE || link.purpose;
          const isActive = link.IS_ACTIVE ?? link.is_active;
          return purpose === "shiori_share" && Boolean(isActive);
        });
        activeLink.value = normalizeActiveLink(active);
      } catch (error) {
        errorMessage.value = getErrorMessage(
          error,
          "共有リンクを取得できませんでした",
        );
      } finally {
        loadingLinks.value = false;
      }
    };

    const ensureShareLink = async () => {
      if (!props.canManage) return "";
      if (createdLink.value) return createdLink.value;
      loading.value = true;
      errorMessage.value = "";
      try {
        const result = await shioriStore.generateShareLink(
          props.shioriId,
          "edit",
        );
        createdLink.value = result.share_url;
        activeLink.value = {
          tokenId: result.token_id,
          expiresAt: result.expires_at || null,
          permissionType: normalizePermissionType(result.permission_type),
        };
        successMessage.value = "共有リンクを作成しました";
        clearMessagesLater();
        return createdLink.value;
      } catch (error) {
        const conflictMetadata =
          error.response?.status === 409
            ? normalizeActiveLink(error.response?.data?.detail)
            : null;
        if (conflictMetadata) {
          activeLink.value = conflictMetadata;
          return "";
        }
        errorMessage.value = getErrorMessage(
          error,
          "共有リンクを作成できませんでした",
        );
        return "";
      } finally {
        loading.value = false;
      }
    };

    const rotateLink = async () => {
      if (!props.canManage) return;
      if (!confirm("現在の共有リンクを無効にして、新しいリンクを発行しますか？")) {
        return;
      }
      loading.value = true;
      errorMessage.value = "";
      try {
        const result = await shioriStore.rotateShareLink(props.shioriId);
        createdLink.value = result.share_url;
        activeLink.value = {
          tokenId: result.token_id,
          expiresAt: result.expires_at || null,
          permissionType: normalizePermissionType(result.permission_type),
        };
        successMessage.value = "新しい共有リンクを発行しました";
      } catch (error) {
        errorMessage.value = getErrorMessage(
          error,
          "共有リンクを再発行できませんでした",
        );
      } finally {
        loading.value = false;
      }
    };

    const revokeLink = async () => {
      if (!props.canManage) return;
      if (!activeLink.value?.tokenId) return;
      if (!confirm("この共有リンクを無効にしますか？")) return;
      loading.value = true;
      errorMessage.value = "";
      try {
        await shioriStore.deleteShareLink(
          props.shioriId,
          activeLink.value.tokenId,
        );
        activeLink.value = null;
        createdLink.value = "";
        successMessage.value = "共有リンクを無効化しました";
      } catch (error) {
        errorMessage.value = getErrorMessage(
          error,
          "共有リンクを無効化できませんでした",
        );
      } finally {
        loading.value = false;
      }
    };

    const formatExpiry = (value) => {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "不明"
        : date.toLocaleString("ja-JP");
    };

    const openNativeShare = async () => {
      const url = await ensureShareLink();
      if (!url || !navigator.share) return;
      try {
        await navigator.share({
          title: "旅行のしおり",
          text: shareText,
          url,
        });
      } catch (error) {
        if (error?.name !== "AbortError") {
          errorMessage.value = "共有メニューを開けませんでした";
        }
      }
    };

    const fallbackCopy = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    };

    const copyLink = async () => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(createdLink.value);
        } else {
          fallbackCopy(createdLink.value);
        }
        successMessage.value = "リンクをコピーしました";
        clearMessagesLater();
      } catch (error) {
        errorMessage.value = "リンクをコピーできませんでした";
      }
    };

    const closeModal = () => {
      createdLink.value = "";
      emit("update:modelValue", false);
      successMessage.value = "";
      errorMessage.value = "";
    };

    watch(
      () => props.modelValue,
      (isOpen) => {
        if (isOpen && !props.canManage) {
          emit("update:modelValue", false);
        } else if (isOpen) {
          fetchExistingLink();
        }
      },
    );

    return {
      loading,
      loadingLinks,
      createdLink,
      activeLink,
      successMessage,
      errorMessage,
      canUseNativeShare,
      ensureShareLink,
      openNativeShare,
      copyLink,
      rotateLink,
      revokeLink,
      formatExpiry,
      closeModal,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modal-content {
  width: min(100%, 560px);
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}

.modal-header,
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
}

.modal-header {
  border-bottom: 1px solid var(--color-border-light);
}

.modal-header h2 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1.35rem;
}

.close-btn {
  border: 0;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.75rem;
  cursor: pointer;
}

.modal-body {
  padding: 1.5rem;
}

.description {
  margin: 0 0 1rem;
  color: var(--color-text-muted);
  line-height: 1.6;
}

.alert {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
}

.alert-success {
  color: #166534;
  background: #dcfce7;
}

.alert-error {
  color: #991b1b;
  background: #fee2e2;
}

.loading-state {
  padding: 2rem;
  color: var(--color-text-muted);
  text-align: center;
}

.create-button,
.native-share-button {
  width: 100%;
}

.share-panel {
  padding: 1rem;
  background: var(--color-background);
  border-radius: 12px;
}

.one-time-notice,
.permission-text,
.expiry-text,
.issued-status {
  margin: 0 0 0.75rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.6;
}

.permission-text {
  color: var(--color-success-dark, #166534);
  font-weight: 700;
}

.issued-status {
  color: var(--color-text);
  font-weight: 700;
}

.metadata-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.revoke-button {
  border: 1px solid #dc2626;
  background: #fff;
  color: #b91c1c;
}

.link-label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.link-row {
  display: flex;
  gap: 0.5rem;
}

.link-input {
  min-width: 0;
  flex: 1;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: white;
}

.copy-button,
.native-share-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.75rem 1rem;
  border: 0;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.copy-button {
  color: white;
  background: #2563eb;
}

.native-share-button {
  margin-top: 1rem;
  color: white;
  background: var(--primary-color);
}

.modal-footer {
  justify-content: flex-end;
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 480px) {
  .link-row {
    flex-direction: column;
  }
}
</style>
